// web/src/App.tsx
import Amplify from '@aws-amplify/core';
import * as Sentry from '@sentry/react';
import LogRocket from 'logrocket';
import React, { Suspense, lazy } from 'react';
import { Route, Switch, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { LogrocketAnalyticsPlatform } from './Analytics/LogrocketAnalyticsPlatform';
import { MixpanelAnalyticsPlatform } from './Analytics/MixpanelAnalyticsPlatform';
import { SurvicateAnalyticsPlatform } from './Analytics/SurvicateAnalyticsPlatform';
import './App.css';
import { AuthProvider } from './Auth/AuthProvider';
import BookHomeView from './BookHome/BookHomeView';
import BookBuilderView from './Builder/BookBuilder/BookBuilderView';
import ChapterBuilderView from './Builder/ChapterBuilder/ChapterBuilderView';
import LoadingIcon from './Components/LoadingIcon';
import OrientationOverlay from './Components/OrientationOverlay';
import { AchievementsProvider } from './Context/AchievementsProvider';
import { AnalyticsProvider, useAnalytics } from './Context/AnalyticsProvider';
import { AudioProvider } from './Context/AudioContext';
import { BalanceProvider } from './Context/BalanceProvider';
import { ChallengeProvider } from './Context/ChallengeProvider';
import { FeedbackProvider, useFeedback } from './Context/FeedbackProvider';
import { ModalProvider } from './Context/ModalProvider';
import { NotificationProvider } from './Context/NotificationProvider';
import { PaymentProvider, usePayments } from './Context/PaymentProvider';
import { ReadingSessionProvider } from './Context/ReadingSessionProvider';
import { RewardProvider } from './Context/RewardProvider';
import { StreakProvider } from './Context/StreakProvider';
import { UISettingsProvider } from './Context/UISettingsProvider';
import ErrorView from './ErrorView';
import AppHeader from './Header/AppHeader';
import LandingView from './LandingView';
import { CoachBotPaymentProvider } from './PaymentProviders/CoachBotPaymentProvider';
import ProtectedRoute from './ProtectedRoute';
import ReadBookView from './ReadBook/ReadBookView';
import MyStatsView from './Stats/MyStatsView';
import { UserType } from './Types/User';

const SelectInterestL1View = lazy(
  () => import('./SelectInterest/SelectInterestL1View'),
);
const SelectArticleView = lazy(
  () => import('./SelectArticle/SelectArticleView'),
);
const ReadArticleView = lazy(() => import('./ReadArticle/ReadArticleView'));
const BuilderView = lazy(() => import('./Builder/BuilderView'));
const SignInFormView = lazy(() => import('./Auth/SignInFormView'));
const SignInWithStudyReelView = lazy(
  () => import('./Auth/SignInWithStudyReelView'),
);
const ForgotPasswordView = lazy(() => import('./Auth/ForgotPasswordView'));
const ResetPasswordView = lazy(() => import('./Auth/ResetPasswordView'));
const MyStoriesView = lazy(() => import('./MyStories/MyStoriesView'));
const RedeemRewardsView = lazy(() => import('./Rewards/RedeemRewardsView'));
const SelectGradeView = lazy(() => import('./Auth/SelectGradeView'));
const PaywallView = lazy(() => import('./Auth/PaywallView'));
const SignUpFormView = lazy(() => import('./Auth/SignUpFormView'));
const ParentDashboardView = lazy(
  () => import('./ParentDashboard/ParentDashboardView'),
);
const ReadingCirclesDashboardView = lazy(
  () => import('./ReadingCircles/ReadingCirclesDashboardView'),
);
const ReadingCircleView = lazy(
  () => import('./ReadingCircles/ReadingCircleView'),
);

interface AppContainerParams {
  children?: React.ReactNode[] | React.ReactNode;
}

export const AppContainer: React.FC<AppContainerParams> = ({ children }) => {
  const location = useLocation();

  return (
    <div className={`App ${location?.pathname} flex flex-col`}>
      <Sentry.ErrorBoundary fallback={<ErrorView />}>
        <AnalyticsProvider>
          <FeedbackProvider>
            <UISettingsProvider>
              <AuthProvider>
                <RewardProvider>
                  <ModalProvider>
                    <ReadingSessionProvider>
                      <AchievementsProvider>
                        <BalanceProvider>
                          <StreakProvider>
                            <PaymentProvider>
                              <AudioProvider>
                                <NotificationProvider>
                                  <ChallengeProvider>
                                    <AppHeader
                                      key={location?.pathname}
                                    ></AppHeader>
                                    <Suspense
                                      fallback={<LoadingIcon fullScreen />}
                                    >
                                      {children}
                                      <ToastContainer
                                        position="bottom-right"
                                        hideProgressBar={true}
                                        closeButton={false}
                                        closeOnClick={true}
                                        autoClose={3000}
                                        theme="colored"
                                      />
                                    </Suspense>
                                    <OrientationOverlay />
                                  </ChallengeProvider>
                                </NotificationProvider>
                              </AudioProvider>
                            </PaymentProvider>
                          </StreakProvider>
                        </BalanceProvider>
                      </AchievementsProvider>
                    </ReadingSessionProvider>
                  </ModalProvider>
                </RewardProvider>
              </AuthProvider>
            </UISettingsProvider>
          </FeedbackProvider>
        </AnalyticsProvider>
      </Sentry.ErrorBoundary>
    </div>
  );
};

interface AppContentParams {}
export const AppContent: React.FC<AppContentParams> = ({}) => {
  return (
    <Switch>
      <Route path="/signin/studyreel" component={SignInWithStudyReelView} />
      <Route path="/signin" component={SignInFormView} />
      <Route path="/signup" component={SignUpFormView} />
      <Route path="/forgot-password" component={ForgotPasswordView} />
      <Route path="/reset-password" component={ResetPasswordView} />
      <Route path="/unlock-access" component={PaywallView} />
      <ProtectedRoute path="/" component={LandingView} exact />
      <ProtectedRoute
        path="/create-my-own-story/:bookId/:chapterId"
        component={ChapterBuilderView}
        supportsAnonymous={true}
        supportUserTypes={[UserType.Student]}
      />
      <ProtectedRoute
        path="/create-my-own-story/:bookId"
        component={BookBuilderView}
        supportsAnonymous={true}
        supportUserTypes={[UserType.Student]}
      />
      <ProtectedRoute
        path="/create-my-own-story"
        component={BuilderView}
        supportsAnonymous={true}
        supportUserTypes={[UserType.Student]}
      />
      <ProtectedRoute
        path="/parent-dashboard"
        component={ParentDashboardView}
        supportUserTypes={[UserType.Parent]}
      />
      <ProtectedRoute
        path="/reading-circles/:circleId"
        component={ReadingCircleView}
        supportUserTypes={[UserType.Parent, UserType.Student]}
      />
      <ProtectedRoute
        path="/reading-circles"
        component={ReadingCirclesDashboardView}
        supportUserTypes={[UserType.Parent, UserType.Student]}
      />
      <ProtectedRoute
        path="/select-grade"
        component={SelectGradeView}
        supportsNonPremium={true}
        supportUserTypes={[UserType.Student]}
      />
      <ProtectedRoute
        path="/library"
        component={SelectInterestL1View}
        supportUserTypes={[UserType.Student]}
      />
      <ProtectedRoute
        path="/choose-story"
        component={SelectArticleView}
        supportUserTypes={[UserType.Student]}
      />
      <Route path="/article/:articleId" component={ReadArticleView} />
      <Route path="/article/:articleId/:slug" component={ReadArticleView} />
      <Route path="/book/:bookId/:chapterId" component={ReadBookView} />
      <Route path="/book/:bookId" component={BookHomeView} />
      <ProtectedRoute
        path="/my-stories"
        component={MyStoriesView}
        supportUserTypes={[UserType.Student]}
      />
      <ProtectedRoute
        path="/redeem-rewards"
        component={RedeemRewardsView}
        supportUserTypes={[UserType.Student]}
      />
      <ProtectedRoute
        path="/my-stats"
        component={MyStatsView}
        supportUserTypes={[UserType.Student]}
      />
    </Switch>
  );
};

interface WebAppContentParams {}
export const WebAppContent: React.FC<WebAppContentParams> = ({}) => {
  const { registerAnalyticsPlatform } = useAnalytics();
  const { registerPaymentProvider } = usePayments();
  const { registerFeedbackProvider } = useFeedback();

  Sentry.isInitialized() ||
    Sentry.init({
      dsn: window.env.REACT_APP_SENTRY_DSN,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
        Sentry.captureConsoleIntegration({
          levels: ['error', 'warn'],
        }),
      ],
      environment: window.env.REACT_APP_ENVIRONMENT,
      release: window.env.REACT_APP_VERSION,
      // Performance Monitoring
      tracesSampleRate: 1.0, //  Capture 100% of the transactions
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: ['localhost', window.env.REACT_APP_API_URL],
      // ReadingSession Replay
      replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
      beforeSend: (event) => {
        console.debug('Sentry beforeSend', event);
        if (!event) return event;

        const logRocketSession = LogRocket?.sessionURL;
        if (logRocketSession !== null) {
          event.extra = event.extra ?? {};
          event.extra['LogRocket'] = logRocketSession;
        }
        return event;
      },
    });

  const PAYMENT_PROVIDER = new CoachBotPaymentProvider();

  // We can't do it in useEffect because we need it to be available very early (e.g. after user comes in from SSO we start listening for SSO Auth event, it happens before effects are executed)
  // Duplicate attempts to register the same provider are fine, they'll just be ignored
  const logrocketProvider = new LogrocketAnalyticsPlatform({
    logrocketAppId: window.env.REACT_APP_LOGROCKET_APP_ID,
    releaseVersion: window.env.REACT_APP_VERSION,
  });
  const survicateProvider = new SurvicateAnalyticsPlatform({
    workspaceKey: window.env.REACT_APP_SURVICATE_WORKSPACE_KEY,
    feedbackSurveyId: window.env.REACT_APP_SURVICATE_FEEDBACK_SURVEY_ID,
    storyFeedbackSurveyId:
      window.env.REACT_APP_SURVICATE_STORY_FEEDBACK_SURVEY_ID,
    bookFeedbackSurveyId:
      window.env.REACT_APP_SURVICATE_BOOK_FEEDBACK_SURVEY_ID,
    bookVideoFeedbackSurveyId:
      window.env.REACT_APP_SURVICATE_BOOK_VIDEO_FEEDBACK_SURVEY_ID,
  });
  registerFeedbackProvider(survicateProvider);

  registerAnalyticsPlatform(survicateProvider);
  registerAnalyticsPlatform(logrocketProvider);
  registerAnalyticsPlatform(
    new MixpanelAnalyticsPlatform({
      mixpanelProjectToken: window.env.REACT_APP_MIXPANEL_PROJECT_TOKEN,
      isProduction: window.env.REACT_APP_ENVIRONMENT === 'production',
    }),
  );

  // Register Survicate for analytics events
  registerPaymentProvider(PAYMENT_PROVIDER);

  return <AppContent />;
};

const WebApp: React.FC = () => {
  Amplify.configure({
    Auth: {
      region: window.env.REACT_APP_REGION,
      userPoolId: window.env.REACT_APP_USER_POOL_ID,
      userPoolWebClientId: window.env.REACT_APP_USER_POOL_CLIENT_ID,
      mandatorySignIn: true,
      authenticationFlowType: 'USER_SRP_AUTH',
      oauth: {
        domain: window.env.REACT_APP_USER_POOL_DOMAIN,
        scope: ['email', 'profile', 'openid'],
        redirectSignIn: new URL(
          // Only /signin and /signup are allowed as redirect URIs from Cognito
          ['/signin/', '/signup/'].find((path) =>
            path.startsWith(window.location.pathname),
          ) || '/signin/',
          window.location.origin,
        ).toString(),
        redirectSignOut: new URL('/signin/', window.location.origin).toString(),
        responseType: 'code',
      },
    },
  });

  return (
    <AppContainer>
      <WebAppContent />
    </AppContainer>
  );
};
export default WebApp;
